import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  MSmartInfoWindowProps,
  InfoWindowPosition,
  PrimaryPosition,
  SecondaryAlignment,
  INFO_WINDOW_CONSTANTS,
} from './MSmartInfoWindow.types';
import * as Styled from './MSmartInfoWindow.styles';

const { WIDTH, HEIGHT, MARKER_HEIGHT, MARKER_TAIL_HEIGHT, DOT_MARKER_SIZE, EDGE_PADDING, GAP } = INFO_WINDOW_CONSTANTS;

/**
 * Calculate the optimal position for the InfoWindow based on available space.
 *
 * The marker's pixel position is at the tail tip (the lat/lng anchor point).
 * - spaceTop: distance from anchor to top of map, minus the full marker height
 * - spaceBottom: distance from anchor to bottom of map (anchor is at tail tip)
 */
const calculateOptimalPosition = (
  markerPixelX: number,
  markerPixelY: number,
  mapWidth: number,
  mapHeight: number
): InfoWindowPosition => {
  const totalWidthNeeded = WIDTH + GAP;
  const totalMarkerHeight = MARKER_HEIGHT + MARKER_TAIL_HEIGHT;

  // Space above: from anchor up, minus the full marker (body + tail)
  const spaceTop = markerPixelY - totalMarkerHeight;
  // Space below: from anchor down (anchor is at tail tip, so full space available)
  const spaceBottom = mapHeight - markerPixelY;
  const spaceLeft = markerPixelX;
  const spaceRight = mapWidth - markerPixelX;

  const hasSpaceTop = spaceTop >= HEIGHT + EDGE_PADDING + GAP;
  const hasSpaceBottom = spaceBottom >= HEIGHT + EDGE_PADDING + GAP;
  const hasSpaceLeft = spaceLeft >= totalWidthNeeded + EDGE_PADDING;
  const hasSpaceRight = spaceRight >= totalWidthNeeded + EDGE_PADDING;

  let primary: PrimaryPosition = 'top';
  let secondary: SecondaryAlignment = 'center';

  if (hasSpaceTop) {
    primary = 'top';
  } else if (hasSpaceBottom) {
    primary = 'bottom';
  } else if (hasSpaceLeft && spaceLeft >= spaceRight) {
    primary = 'left';
  } else if (hasSpaceRight) {
    primary = 'right';
  } else {
    primary = spaceTop >= spaceBottom ? 'top' : 'bottom';
  }

  if (primary === 'top' || primary === 'bottom') {
    const halfWidth = WIDTH / 2;
    const hasSpaceLeftForCenter = markerPixelX >= halfWidth + EDGE_PADDING;
    const hasSpaceRightForCenter = mapWidth - markerPixelX >= halfWidth + EDGE_PADDING;

    if (hasSpaceLeftForCenter && hasSpaceRightForCenter) {
      secondary = 'center';
    } else if (!hasSpaceLeftForCenter && hasSpaceRightForCenter) {
      secondary = 'start';
    } else if (hasSpaceLeftForCenter && !hasSpaceRightForCenter) {
      secondary = 'end';
    } else {
      secondary = markerPixelX < mapWidth / 2 ? 'start' : 'end';
    }
  } else {
    const halfHeight = HEIGHT / 2;
    const hasSpaceTopForCenter = markerPixelY >= halfHeight + EDGE_PADDING;
    const hasSpaceBottomForCenter = mapHeight - markerPixelY >= halfHeight + EDGE_PADDING;

    if (hasSpaceTopForCenter && hasSpaceBottomForCenter) {
      secondary = 'center';
    } else if (!hasSpaceTopForCenter && hasSpaceBottomForCenter) {
      secondary = 'start';
    } else if (hasSpaceTopForCenter && !hasSpaceBottomForCenter) {
      secondary = 'end';
    } else {
      secondary = markerPixelY < mapHeight / 2 ? 'start' : 'end';
    }
  }

  return { primary, secondary };
};

/**
 * Convert lat/lng to pixel coordinates within the map's container.
 *
 * The returned coordinates are relative to the map's `getDiv()`. Because we
 * portal into a positioned ancestor (e.g. `ChatMapContainer`) that wraps the
 * map div, these coordinates also work as absolute offsets within the portal
 * container as long as the map div is anchored at (0, 0) inside that wrapper.
 */
const getMarkerPixelPosition = (map: google.maps.Map, lat: number, lng: number): { x: number; y: number } | null => {
  const bounds = map.getBounds();
  const projection = map.getProjection();

  if (!bounds || !projection) return null;

  const mapDiv = map.getDiv();
  if (!mapDiv) return null;

  const mapWidth = mapDiv.offsetWidth;
  const mapHeight = mapDiv.offsetHeight;

  const markerLatLng = new google.maps.LatLng(lat, lng);
  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
  const markerPoint = projection.fromLatLngToPoint(markerLatLng);

  if (!topRight || !bottomLeft || !markerPoint) return null;

  const worldWidth = topRight.x - bottomLeft.x;
  const worldHeight = bottomLeft.y - topRight.y;

  const x = ((markerPoint.x - bottomLeft.x) / worldWidth) * mapWidth;
  const y = ((markerPoint.y - topRight.y) / worldHeight) * mapHeight;

  return { x, y };
};

/**
 * MSmartInfoWindow - A smart InfoWindow component that positions itself
 * optimally based on available space around the marker.
 *
 * Rendered via `ReactDOM.createPortal` into a positioned ancestor of the
 * map (passed via `portalContainer`). This escapes Google Maps' overlay
 * pane stacking context so the InfoWindow can render above markers,
 * clusters, map filter chips, and the mobile list toggle.
 *
 * Marker pixel coordinates are recomputed on map `bounds_changed`,
 * `zoom_changed`, `center_changed`, and `idle` events as well as on window
 * `resize`, so the InfoWindow follows the marker during pan/zoom.
 */
export const MSmartInfoWindow: React.FC<MSmartInfoWindowProps> = ({
  marker,
  map,
  children,
  portalContainer,
  isSinglePropertyResponse = false,
}) => {
  const [position, setPosition] = useState<InfoWindowPosition>({
    primary: 'top',
    secondary: 'center',
  });
  const [pixelPosition, setPixelPosition] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const markerHalfWidth = useMemo(() => {
    // Single property uses a 18px circle dot marker
    if (isSinglePropertyResponse) {
      return DOT_MARKER_SIZE / 2; // 9px
    }
    // Regular price pill marker
    const priceText = marker.priceLabel || marker.rateUnavailableLabel || '$000';
    const padding = 8; // 4px padding on each side
    const aiIconWidth = marker.isAIRecommended ? 16 : 0; // 14px icon + 2px margin
    const textWidth = priceText.length * 8; // ~8px per character for this font
    const estimatedWidth = padding + aiIconWidth + textWidth;
    return Math.round(estimatedWidth / 2);
  }, [marker.priceLabel, marker.rateUnavailableLabel, marker.isAIRecommended, isSinglePropertyResponse]);

  const updatePosition = useCallback(() => {
    if (!map) return;

    const pixelPos = getMarkerPixelPosition(map, marker.latitude, marker.longitude);
    if (!pixelPos) return;

    const mapDiv = map.getDiv();
    if (!mapDiv) return;

    const mapWidth = mapDiv.offsetWidth;
    const mapHeight = mapDiv.offsetHeight;

    setPixelPosition(pixelPos);
    setPosition(calculateOptimalPosition(pixelPos.x, pixelPos.y, mapWidth, mapHeight));
  }, [map, marker.latitude, marker.longitude]);

  useEffect(() => {
    if (!map) return;
    updatePosition();
  }, [map, updatePosition]);

  useEffect(() => {
    updatePosition();
  }, [marker.latitude, marker.longitude, updatePosition]);

  // Subscribe to map events so the InfoWindow tracks the marker during
  // pan/zoom. OverlayView used to do this for free; with a portal we need
  // to recompute pixel coordinates manually.
  useEffect(() => {
    if (!map) return;

    const listeners: google.maps.MapsEventListener[] = [
      map.addListener('bounds_changed', updatePosition),
      map.addListener('zoom_changed', updatePosition),
      map.addListener('center_changed', updatePosition),
      map.addListener('idle', updatePosition),
    ];

    return () => {
      listeners.forEach(listener => google.maps.event.removeListener(listener));
    };
  }, [map, updatePosition]);

  // Recompute on viewport resize so the InfoWindow follows layout changes.
  useEffect(() => {
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updatePosition]);

  if (!map || !portalContainer || !pixelPosition) return null;

  return ReactDOM.createPortal(
    <Styled.InfoWindowContainer
      ref={containerRef}
      $primary={position.primary}
      $secondary={position.secondary}
      $markerHalfWidth={markerHalfWidth}
      $markerPixelX={pixelPosition.x}
      $markerPixelY={pixelPosition.y}
      $isSinglePropertyResponse={isSinglePropertyResponse}
      data-testid="smart-info-window"
    >
      {children}
    </Styled.InfoWindowContainer>,
    portalContainer
  );
};

export default MSmartInfoWindow;
